import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  retainSearchParams,
} from "@tanstack/react-router";
import appCss from "../styles.css?url";
import criticalCss from "../critical.css?inline";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { GoogleTranslateInit } from "@/components/google-translate-init";
import { GtmContainerDeferred } from "@/components/gtm-container-deferred";
import { LanguageSuggestionDialog } from "@/components/language-suggestion-dialog";
import { NotFoundPage } from "@/components/not-found";
import { ANDERS_ID, ANGELA_ID } from "@/lib/authors";

// Marketing attribution params that must persist across client-side
// navigations and redirects so analytics/CRM can attribute the session.
const TRACKING_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
  "msclkid",
  "mc_cid",
  "mc_eid",
  "ref",
] as const;

const trackingBootstrapScript = `(function(){try{var landingSearch=window.location.search||'';if(!landingSearch||landingSearch.length<2)return;var keys=${JSON.stringify([...TRACKING_PARAMS])};var landingParams=new URLSearchParams(landingSearch);var captured={};var found=false;keys.forEach(function(k){if(landingParams.has(k)){captured[k]=landingParams.get(k);found=true;}});if(!found)return;try{var existing=JSON.parse(window.sessionStorage.getItem('tracking_params')||'{}');var merged=Object.assign({},existing,captured);merged.__captured_at=merged.__captured_at||new Date().toISOString();merged.__landing_path=merged.__landing_path||window.location.pathname;merged.__landing_search=merged.__landing_search||landingSearch;window.sessionStorage.setItem('tracking_params',JSON.stringify(merged));}catch(e){}var mergeUrl=function(url){if(typeof url==='undefined'||url===null)return url;try{var base=window.location.href;var next=new URL(String(url),base);if(next.origin!==window.location.origin)return url;var changed=false;keys.forEach(function(k){if(landingParams.has(k)&&!next.searchParams.has(k)){next.searchParams.set(k,landingParams.get(k));changed=true;}});if(!changed)return url;if(typeof url==='string'&&url.charAt(0)==='/'){return next.pathname+next.search+next.hash;}return next.href;}catch(e){return url;}};if(window.history&&window.history.replaceState){var originalReplace=window.history.replaceState;window.history.replaceState=function(state,title,url){return originalReplace.call(window.history,state,title,mergeUrl(url));};}if(window.history&&window.history.pushState){var originalPush=window.history.pushState;window.history.pushState=function(state,title,url){return originalPush.call(window.history,state,title,mergeUrl(url));};}if(window.history&&window.history.replaceState){window.history.replaceState(window.history.state,'',window.location.pathname+landingSearch+window.location.hash);}}catch(e){}})();`;

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-serif text-3xl text-foreground">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted-foreground">Try again or head back home.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground"
          >
            Try again
          </button>
          <a href="/" className="rounded-full border border-border bg-background px-5 py-2 text-sm font-medium text-foreground">
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

const organizationSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": "https://saased.co/#website",
      "name": "SaaSed",
      "alternateName": "SaaSed.co",
      "url": "https://saased.co/",
      "description":
        "SaaSed is a specialist Salesforce procurement and commercial advisory company. We work with enterprise buyers on Salesforce renewals, contract negotiation, licensing decisions, commercial strategy and cost optimisation — with the aim of improving commercial control and removing cost that serves no purpose.",


      "inLanguage": "en",
      "publisher": { "@id": "https://saased.co/#organization" }
    },
    {
      "@type": ["Organization", "ProfessionalService"],
      "@id": "https://saased.co/#organization",
      "name": "SaaSed",
      "legalName": "SaaSed.co Limited",
      "url": "https://saased.co/",
      "logo": {
        "@type": "ImageObject",
        "@id": "https://saased.co/#logo",
        "url": "https://saased.co/favicon-v2.png",
        "contentUrl": "https://saased.co/favicon-v2.png",
        "caption": "SaaSed"
      },
      "image": { "@id": "https://saased.co/#logo" },
      "description":
        "SaaSed is a specialist Salesforce procurement and commercial advisory company. We work with enterprise buyers on Salesforce renewals, contract negotiation, licensing decisions, commercial strategy and cost optimisation — with the aim of improving commercial control and removing cost that serves no purpose.",


      "sameAs": [
        "https://www.linkedin.com/company/saasedco/",
        "https://www.instagram.com/saased.co"
      ],
      "founder": [{ "@id": ANDERS_ID }, { "@id": ANGELA_ID }],
      "knowsAbout": [
        "Salesforce Procurement",
        "Salesforce Renewals",
        "Salesforce Contract Negotiation",
        "Salesforce Licensing",
        "Salesforce Cost Optimization",
        "Salesforce Commercial Strategy",
        "Salesforce SKU Optimization",
        "Salesforce Audit Services",
        "Salesforce Pricing Benchmarks",
        "SELA Negotiation Support",
        "AELA Negotiation Support"
      ],

      "areaServed": {
        "@type": "GeoShape",
        "name": "EMEA"
      }
    },
    {
      "@type": "Person",
      "@id": ANDERS_ID,
      "name": "Anders Gustafsson",
      "jobTitle": "Co-Founder & Salesforce Expert",
      "description": "Anders is a Co-Founder of SaaSed.co and a Salesforce expert who spent eight years inside Salesforce.",
      "url": "https://saased.co/blog/author/anders-gustafsson",
      "sameAs": ["https://www.linkedin.com/in/andersgustafsson1/"],
      "worksFor": { "@id": "https://saased.co/#organization" }
    },
    {
      "@type": "Person",
      "@id": ANGELA_ID,
      "name": "Angela Lai",
      "jobTitle": "Co-Founder & CEO",
      "description": "Angela is a co-founder and the CEO of SaaSed.",
      "url": "https://saased.co/blog/author/angela-lai",
      "sameAs": ["https://www.linkedin.com/in/angelabolai/"],
      "worksFor": { "@id": "https://saased.co/#organization" }
    },
    {
      "@type": "Service",
      "@id": "https://saased.co/#service-salesforce-negotiation",
      "name": "Salesforce Contract Negotiation",
      "serviceType": "Salesforce Renewal Consulting",
      "url": "https://saased.co/services",
      "provider": { "@id": "https://saased.co/#organization" },
      "areaServed": { "@type": "GeoShape", "name": "EMEA" },
      "description":
        "Expert negotiation support to secure the best Salesforce contract terms and pricing."
    },
    {
      "@type": "Service",
      "@id": "https://saased.co/#service-software-audit",
      "name": "Software Licence Audit",
      "serviceType": "SaaS Licence Audit",
      "url": "https://saased.co/services",
      "provider": { "@id": "https://saased.co/#organization" },
      "areaServed": { "@type": "GeoShape", "name": "EMEA" },
      "description":
        "Audit of Salesforce licence usage and entitlements, independent of Salesforce, to remove waste before renewal."
    },
    {
      "@type": "Service",
      "@id": "https://saased.co/#service-sku-review",
      "name": "SKU and Pricing Review",
      "serviceType": "Salesforce SKU Optimization",
      "url": "https://saased.co/services",
      "provider": { "@id": "https://saased.co/#organization" },
      "areaServed": { "@type": "GeoShape", "name": "EMEA" },
      "description":
        "Line-by-line review of Salesforce SKUs and pricing against benchmarks to right-size the contract."
    }
  ]
};


export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  validateSearch: (search: Record<string, unknown>) => search,
  search: {
    middlewares: [retainSearchParams([...TRACKING_PARAMS])],
  },
  head: ({ matches }) => {
    // A blog article page shows its own cover as the share image, so the
    // sitewide image's dimensions and alt must not describe that page.
    const isArticlePage = matches.some((m) => {
      const match = m as { routeId?: unknown; _notFound?: unknown; loaderData?: unknown };
      if (String(match.routeId ?? "") !== "/blog/$slug") return false;
      if (match._notFound) return false;
      return Boolean((match.loaderData as { post?: unknown } | undefined)?.post);
    });
    return {
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "google-site-verification", content: "vhmPIwwuRrgl5FZv2453aaifgLniXmaiVkaLAw0pLCc" },
      { name: "msvalidate.01", content: "B0295D302983C1C98592DD22EAD30469" },
      { title: "SaaSed — Salesforce Procurement Specialists" },
      { name: "description", content: "SaaSed is a specialist Salesforce procurement and commercial advisory company helping enterprise buyers with renewals, negotiation and cost optimisation." },
      { name: "author", content: "SaaSed" },
      { name: "ai-agent-readiness", content: "enabled" },
      { name: "web-mcp-version", content: "1.0" },
      { property: "og:title", content: "SaaSed — Salesforce Procurement & Commercial Advisory" },
      { property: "og:description", content: "SaaSed is a specialist Salesforce procurement and commercial advisory company helping enterprise buyers with renewals, negotiation and cost optimisation." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "SaaSed — Salesforce Procurement & Commercial Advisory" },
      { name: "twitter:description", content: "SaaSed is a specialist Salesforce procurement and commercial advisory company helping enterprise buyers with renewals, negotiation and cost optimisation." },

      { property: "og:image", content: "https://saased.co/saased-share-image.png" },
      { name: "twitter:image", content: "https://saased.co/saased-share-image.png" },
      ...(isArticlePage
        ? []
        : [
            { property: "og:image:width", content: "1200" },
            { property: "og:image:height", content: "630" },
            { property: "og:image:alt", content: "SaaSed: Salesforce Procurement Advisory" },
          ]),
    ],
    links: [
      // The main stylesheet loads synchronously so the first paint is already
      // fully styled. The previous media="print" swap trick painted an
      // unstyled tree and then reflowed it, which was the dominant source of
      // desktop CLS. Critical CSS stays inlined as a fast-path for tokens.
      { rel: "preload", href: appCss, as: "style" },
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico?v=2" },
      { rel: "icon", type: "image/png", href: "/favicon-v2.png" },
      { rel: "apple-touch-icon", href: "/favicon-v2.png" },
      // Preconnect to Google Fonts host used by @font-face URLs in styles.css (Caveat)
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      // Preload ONLY the two above-the-fold fonts (display + body). Extra font
      // preloads compete with the LCP image and hurt mobile performance.
      { rel: "preload", as: "font", type: "font/woff2", href: "/__l5e/assets-v1/4d151f37-2d28-431b-a676-9b15f4e9acd7/BricolageGrotesque.woff2", crossOrigin: "anonymous", fetchPriority: "high" },
      { rel: "preload", as: "font", type: "font/woff2", href: "/__l5e/assets-v1/eed12b57-a877-4679-96ff-9b8cd1565e24/DMSans.woff2", crossOrigin: "anonymous", fetchPriority: "high" },
      // Standards-based Agent Resource Discovery pointer.
      { rel: "ai-catalog", type: "application/json", href: "/.well-known/ai-catalog.json" },

    ],
    styles: [
      { children: criticalCss },
    ],
    scripts: [
      {
        // Capture marketing/tracking params into sessionStorage BEFORE any
        // GTM or client-side routing runs, so GA4 reads the original landing
        // URL and downstream navigations/forms can still access attribution.
        children: trackingBootstrapScript,
      },
      {
        // dataLayer only. The GTM container itself is loaded after FCP by
        // <GtmContainerDeferred /> to keep Total Blocking Time low.
        children: `window.dataLayer=window.dataLayer||[];`,
      },


      {
        type: "application/ld+json",
        children: JSON.stringify(organizationSchema),
      },
    ],
    };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundPage,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head><HeadContent /></head>
      <body>
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-WVRF6HQN"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <div className="flex min-h-screen flex-col">
        <SiteHeader />
        <main id="main-content" aria-label="Main content" className="flex-1 min-h-screen"><Outlet /></main>
        <SiteFooter />
      </div>
      <GoogleTranslateInit />
      <GtmContainerDeferred />
      <LanguageSuggestionDialog />
    </QueryClientProvider>
  );
}
